<template>
    <div class="ajustes-detail-container">
        <!-- Estado de carga -->
        <div v-if="isLoading" class="text-center py-5">
            <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
            <p class="mt-2 text-muted">{{ internalLabels.loading || 'Cargando...' }}</p>
        </div>

        <!-- Tabla de origen con CorporateDataTable -->
        <div
            v-if="!isLoading && ajustOrigen && ajustOrigen.length > 0"
            class="ajustes-detail-table-container"
        >
            <CorporateDataTable
                :items="ajustOrigen"
                :fields="tableFields"
                :labels="tableLabels"
                :default-per-page="defaultPerPage"
                :page-options="pageOptions"
                :narrow="false"
                :table-min-height="490"
                :table-max-height="490"
                :show-search="true"
                :allow-column-toggle="true"
                :enable-sorting="true"
                :filter-defs="tableFilterDefs"
                :toolbar-disabled="false"
                :show-back-button="showBackButton"
                :back-button-text="internalLabels.volver || 'Volver'"
                @back="goBack"
                class="ajustes-detail-table"
            >
                <template #toolbar-actions>
                    <div class="detail-toolbar-left d-flex align-items-center">
                        <!-- ESPACIO VACÍO - EL BOTÓN DIFERENCIA AHORA ESTÁ EN EL PADRE -->
                    </div>
                    <div class="detail-toolbar-right d-flex align-items-center">
                        <!-- Botones solo visibles si NO es readOnly y opción es 2 (Modificar) -->
                        <template v-if="!readOnly && infoOpcion === 2">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn table-toolbar-add-btn-origin-ajuste ml-2"
                                @click="abrirContraAjuste()"
                            >
                                <font-awesome-icon :icon="['fas', 'tune']" class="mr-1" />
                                {{ internalLabels.ajustar || 'Ajustar' }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn table-toolbar-add-btn-origin-all ml-2"
                                @click="ajustarTodo()"
                            >
                                <font-awesome-icon :icon="['fas', 'playlist-add-check']" class="mr-1" />
                                {{ internalLabels.ajustarTodo || 'Ajustar Todo' }}
                            </b-button>
                        </template>
                    </div>
                </template>

                <!-- Estado vacío -->
                <template #empty-state>
                    <div class="text-center py-5">
                        <font-awesome-icon :icon="['fas', 'file-alt']" size="4x" class="text-muted mb-3" style="opacity: 0.3;" />
                        <p class="h5 text-muted">{{ internalLabels.noData || 'No hay cuentas de origen registradas' }}</p>
                    </div>
                </template>

                <!-- Columnas -->
                <template #cell(cuenta)="data">
                    <div class="text-left font-weight-bold">{{ data.value }}</div>
                </template>

                <template #cell(descripcion)="data">
                    <div class="text-left">{{ data.value }}</div>
                </template>

                <template #cell(saldo_actual)="data">
                    <div class="text-right">{{ formatCurrency(data.value) }}</div>
                </template>

                <template #cell(funcional1)="data">
                    <div class="text-right">{{ formatCurrency(data.value) }}</div>
                </template>

                <template #cell(funcional2)="data">
                    <div class="text-right">{{ formatCurrency(data.value) || '-' }}</div>
                </template>

                <template #cell(ajuste_actual)="data">
                    <div class="text-right">{{ formatCurrency(data.value) }}</div>
                </template>

                <template #cell(ajuste_funcional1)="data">
                    <div class="text-right">{{ formatCurrency(data.value) }}</div>
                </template>

                <!-- Columna Editar - SOLO visible si NO es readOnly y es Modificar -->
                <template #cell(accionEditar)="data">
                    <b-button
                        v-if="!readOnly && infoOpcion === 2"
                        style="background-color: #FF924C; width: 30px; height: 30px; border: none; border-radius: 4px;"
                        class="md-fab md-primary table-action-btn"
                        @click="editAjuste(data.item)"
                    >
                        <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs" />
                    </b-button>
                </template>
            </CorporateDataTable>
        </div>

        <!-- Mensaje sin datos -->
        <div v-if="!isLoading && (!ajustOrigen || ajustOrigen.length === 0)" class="admin-table-block">
            <h5>{{ internalLabels.noData || 'No hay cuentas de origen registradas' }}</h5>
        </div>

        <!-- Modal de edición -->
        <b-modal v-model="showDialogEdit" :title="tituloModal" size="lg" @hidden="showDialogEdit = false">
            <b-form @submit.prevent="guardarAjuste">
                <b-row>
                    <b-col cols="2"><h6><b>{{ labels.cuenta }}</b></h6></b-col>
                    <b-col cols="2"><h6>{{ formEdit?.cuenta }}</h6></b-col>
                    <b-col cols="8"><h6>{{ formEdit?.descripcion }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="2"><h6><b>{{ labels.saldo_actual }}</b></h6></b-col>
                    <b-col cols="2"></b-col>
                    <b-col cols="8"><h6>{{ formatCurrency(formEdit?.saldo_actual) }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.funcional1 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.promedio1 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.interna1 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.funcional1) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.promedio1) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.interna1) }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.funcional2 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.promedio2 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.interna2 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.funcional2) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.promedio2) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.interna2) }}</h6></b-col>
                </b-row>
                <b-row class="mt-3">
                    <b-col cols="3"><h6><b>{{ labels.ajuste_actual }}</b></h6></b-col>
                    <b-col cols="9">
                        <b-form-input
                            v-model="v_ajuste_actual"
                            type="number"
                            step="0.01"
                            @input="calcularAjustes"
                            :disabled="readOnly || eliminar"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_funcional1 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_promedio1 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_interna1 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_funcional1) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_promedio1) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_interna1) }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_funcional2 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_promedio2 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_interna2 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_funcional2) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_promedio2) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_interna2) }}</h6></b-col>
                </b-row>
            </b-form>

            <template #footer>
                <b-button v-if="!eliminar" variant="secondary" @click="restaurarAjuste">
                    {{ labels.restaurar || 'Restaurar' }}
                </b-button>
                <b-button v-if="!eliminar" variant="primary" @click="guardarAjuste" :disabled="readOnly">
                    {{ labels.guardar || 'Guardar' }}
                </b-button>
                <b-button v-if="eliminar" variant="danger" @click="eliminarAjuste" :disabled="readOnly">
                    {{ labels.eliminar || 'Eliminar' }}
                </b-button>
                <b-button variant="secondary" @click="showDialogEdit = false">
                    {{ labels.cerrar || 'Cerrar' }}
                </b-button>
            </template>
        </b-modal>

        <!-- Modal de Contra Ajuste -->
        <b-modal v-model="showContraAjuste" :title="labels.ajuste_consolidacion || 'Ajuste de Consolidación'" size="lg">
            <div class="text-center mb-4">
                <h5>{{ labels.seleccionar_destino || 'Seleccione dónde aplicar el ajuste:' }}</h5>
            </div>
            <b-row class="mb-3">
                <b-col cols="6" class="text-center">
                    <b-button
                        :variant="formAjuste.origen ? 'primary' : 'outline-secondary'"
                        class="w-100 py-3"
                        @click="seleccionarOrigen()"
                        :disabled="readOnly"
                    >
                        <font-awesome-icon :icon="['fas', 'arrow-right']" class="mr-2" />
                        {{ nombreCompania || 'Origen' }}
                        <br><small>{{ labels.distribuir_origen || 'Distribuir en cuentas' }}</small>
                        <br><small class="text-muted">{{ cuentasOrigen }} cuentas</small>
                    </b-button>
                </b-col>
                <b-col cols="6" class="text-center">
                    <b-button
                        :variant="formAjuste.destino ? 'primary' : 'outline-secondary'"
                        class="w-100 py-3"
                        @click="seleccionarDestino()"
                        :disabled="readOnly"
                    >
                        <font-awesome-icon :icon="['fas', 'arrow-left']" class="mr-2" />
                        {{ nombreCompaniaDestino || 'Destino' }}
                        <br><small>{{ labels.una_cuenta || '1 cuenta' }}</small>
                    </b-button>
                </b-col>
            </b-row>
            <b-row v-if="showSummary" class="mt-3">
                <b-col cols="12">
                    <div class="alert alert-info">
                        <font-awesome-icon :icon="['fas', 'info-circle']" class="mr-2" />
                        {{ labels.diferencia_actual || 'Diferencia actual:' }}
                        <strong>{{ formatCurrency(diferenciaTotal) }}</strong>
                    </div>
                </b-col>
            </b-row>

            <template #footer>
                <b-button variant="primary" @click="aplicarAjusteAutomatico" :disabled="readOnly">
                    <font-awesome-icon :icon="['fas', 'check']" class="mr-1" />
                    {{ labels.aplicar_ajuste || 'Aplicar Ajuste Automático' }}
                </b-button>
                <b-button variant="secondary" @click="showContraAjuste = false">
                    {{ labels.cancelar || 'Cancelar' }}
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import Balance from "@/services/BalanceNiif"
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
    name: "EliminaOrigen",
    components: { CorporateDataTable },
    props: {
        ajustOrigen: { type: Array, default: () => [] },
        codReporte: { type: [String, Number], default: null },
        id_compania: { type: [String, Number], default: null },
        vigencia: { type: [String, Number], default: null },
        nombreCompania: { type: String, default: '' },
        nombreCompaniaDestino: { type: String, default: '' },
        readOnly: { type: Boolean, default: false },
        infoOpcion: { type: Number, default: 0 },
        infoSecuencia: { type: [String, Number], default: null },
        infoTipoAjuste: { type: Number, default: 0 },
        infoIdCompania: { type: [String, Number], default: null },
        infoIdCompania1: { type: [String, Number], default: null },
        infoVigencia: { type: [String, Number], default: null },
        tableLabels: { type: Object, default: () => ({}) },
        tableFilterDefs: { type: Array, default: () => [] },
        defaultPerPage: { type: Number, default: 10 },
        pageOptions: { type: Array, default: () => [10, 20, 50, 100] },
        showBackButton: { type: Boolean, default: false }
    },
    data: () => ({
        showDialogEdit: false,
        showContraAjuste: false,
        formEdit: {},
        v_ajuste_actual: 0,
        v_ajuste_funcional1: 0,
        v_ajuste_promedio1: 0,
        v_ajuste_interna1: 0,
        v_ajuste_funcional2: 0,
        v_ajuste_promedio2: 0,
        v_ajuste_interna2: 0,
        eliminar: false,
        isLoading: false,
        formAjuste: { origen: true, destino: false },
        diferenciaTotal: 0,
        showSummary: false,
        mostrarDiferenciaFlag: false,
        valor_ajustar_funcional1: 0,
        cuentasOrigen: 0,
        totalOrigen: 0,
        totalDestino: 0,
    }),
    computed: {
        internalLabels() {
            const es = this.$store.state._language == "es-ES"
            return {
                ...this.tableLabels,
                cuenta: es ? "Cuenta" : "Account",
                descripcion: es ? "Descripción" : "Description",
                saldo_actual: es ? "Saldo" : "Balance",
                funcional1: es ? "Funcional" : "Functional",
                funcional2: es ? "Funcional 2" : "Functional 2",
                promedio1: es ? "Promedio" : "Average",
                promedio2: es ? "Promedio 2" : "Average 2",
                interna1: es ? "Interna" : "Internal",
                interna2: es ? "Interna 2" : "Internal 2",
                ajuste_actual: es ? "Ajuste Saldo" : "Balance Adjustment",
                ajuste_funcional1: es ? "Ajuste Funcional" : "Functional Adjustment",
                ajuste_promedio1: es ? "Ajuste Promedio" : "Average Adjustment",
                ajuste_interna1: es ? "Ajuste Interna" : "Internal Adjustment",
                ajuste_funcional2: es ? "Ajuste Funcional 2" : "Functional Adjustment 2",
                ajuste_promedio2: es ? "Ajuste Promedio 2" : "Average Adjustment 2",
                ajuste_interna2: es ? "Ajuste Interna 2" : "Internal Adjustment 2",
                editar: es ? "Editar" : "Edit",
                eliminar: es ? "Eliminar" : "Delete",
                restaurar: es ? "Restaurar" : "Restore",
                guardar: es ? "Guardar" : "Save",
                cerrar: es ? "Cerrar" : "Close",
                diferencia: es ? "Diferencia" : "Difference",
                ajustar: es ? "Ajustar" : "Adjust",
                ajustarTodo: es ? "Ajustar Todo" : "Adjust All",
                ajuste_consolidacion: es ? "Ajuste de Consolidación" : "Consolidation Adjustment",
                seleccionar_destino: es ? "Seleccione dónde aplicar el ajuste:" : "Select where to apply the adjustment:",
                distribuir_origen: es ? "Distribuir en cuentas" : "Distribute in accounts",
                una_cuenta: es ? "1 cuenta" : "1 account",
                diferencia_actual: es ? "Diferencia actual:" : "Current difference:",
                aplicar_ajuste: es ? "Aplicar Ajuste Automático" : "Apply Automatic Adjustment",
                cancelar: es ? "Cancelar" : "Cancel",
                volver: es ? "Volver" : "Back",
                noData: es ? "No hay cuentas de origen registradas" : "No origin accounts registered",
                loading: es ? "Cargando..." : "Loading...",
            }
        },
        labels() { return this.internalLabels },
        tableFields() {
            const fields = [
                { key: 'cuenta', label: this.internalLabels.cuenta, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '120px' },
                { key: 'descripcion', label: this.internalLabels.descripcion, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '200px' },
                { key: 'saldo_actual', label: this.internalLabels.saldo_actual, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'funcional1', label: this.internalLabels.funcional1, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'funcional2', label: this.internalLabels.funcional2, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'ajuste_actual', label: this.internalLabels.ajuste_actual, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'ajuste_funcional1', label: this.internalLabels.ajuste_funcional1, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'accionEditar', label: '', thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '60px' }
            ]
            return fields
        },
        tituloModal() {
            const tipo = this.tipoAjusteTexto(this.formEdit?.tipo_ajuste)
            return `${this.nombreCompania || 'Origen'} ${tipo}`
        }
    },
    methods: {
        goBack() { this.$emit('back') },

        tipoAjusteTexto(tipo) {
            const es = this.$store.state._language == "es-ES"
            if (tipo == 1) return es ? 'Interes no controlante' : 'Non-controlling interest'
            if (tipo == 2) return es ? 'Eliminación en inversión' : 'Investment elimination'
            if (tipo == 3) return es ? 'Eliminación saldo recíproco' : 'Reciprocal balance elimination'
            if (tipo == 4) return es ? 'Ajuste del Combinado' : 'Combined adjustment'
            return es ? 'Tipo desconocido' : 'Unknown type'
        },

        formatCurrency(value) {
            if (value === 0 || value === "0" || value === null || value === "" || value === undefined) return ""
            return new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
        },

        // =============================================
        // CALCULAR DIFERENCIA - Emite el valor al padre
        // =============================================
        calcularDiferencia() {
            let totalOrigen = 0;
            let totalDestino = 0;
            
            // Sumar ajustes de ORIGEN
            if (this.ajustOrigen) {
                this.ajustOrigen.forEach(item => {
                    totalOrigen += parseFloat(item.ajuste_funcional1) || 0;
                });
            }

            if (this.$parent && this.$parent.ajusteDesti) {
                this.$parent.ajusteDesti.forEach(item => {
                    totalDestino += parseFloat(item.ajuste_funcional1) || 0;
                });
            }
            
            const diferencia = -(totalOrigen + totalDestino);
            const diferenciaRedondeada = Math.round(diferencia * 100) / 100;

            this.$emit('diferenciaCalculada', diferenciaRedondeada);
            return diferenciaRedondeada;
        },

        // =============================================
        // VERIFICAR Y ACTUALIZAR VALORES 0 A 1
        // =============================================
        async verificarYActualizarValoresCero(item, tipo) {
            const camposAverificar = [
                'saldo_actual', 'funcional1', 'funcional2',
                'promedio1', 'promedio2', 'interna1', 'interna2',
                'ajuste_actual', 'ajuste_funcional1', 'ajuste_funcional2',
                'ajuste_promedio1', 'ajuste_promedio2', 'ajuste_interna1', 'ajuste_interna2'
            ];

            let hayCeros = false;
            for (const campo of camposAverificar) {
                if (item[campo] === 0 || item[campo] === "0") {
                    hayCeros = true;
                    break;
                }
            }

            if (!hayCeros) return false;

            const valoresActualizados = { ...item };
            for (const campo of camposAverificar) {
                if (valoresActualizados[campo] === 0 || valoresActualizados[campo] === "0") {
                    valoresActualizados[campo] = 1;
                }
            }

            try {
                await Balance.actualizarValoresCero(tipo, valoresActualizados);
                this.$toast.info("Se han actualizado valores de 0 a 1 para evitar divisiones por cero");
                return true;
            } catch (error) {
                this.$toast.error("Error al actualizar valores");
                console.error("Error:", error);
                return false;
            }
        },

        // =============================================
        // OBTENER ITEM ACTUALIZADO
        // =============================================
        async obtenerItemActualizado(itemOriginal) {
            try {
                const respuesta = await Balance.obtenerItemActualizado(
                    this.infoVigencia || this.vigencia,
                    this.infoSecuencia,
                    itemOriginal.cuenta,
                    "O"
                );
                if (respuesta.data && respuesta.data.item) {
                    return respuesta.data.item;
                }
                return null;
            } catch (error) {
                console.error("Error al obtener item actualizado:", error);
                return null;
            }
        },

        // =============================================
        // EDITAR AJUSTE
        // =============================================
        async editAjuste(item) {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para edición.' : 'The validity is disabled for editing.')
                return
            }

            this.eliminar = item.origen === "C"

            try {
                const actualizado = await this.verificarYActualizarValoresCero(item, "O");
                if (actualizado) {
                    const itemActualizado = await this.obtenerItemActualizado(item);
                    if (itemActualizado) {
                        this.formEdit = { ...itemActualizado };
                    } else {
                        this.formEdit = { ...item };
                    }
                } else {
                    this.formEdit = { ...item };
                }
            } catch (error) {
                console.error("Error al actualizar valores:", error);
                this.formEdit = { ...item };
            }

            this.v_ajuste_actual = this.formEdit.ajuste_actual || 0
            this.v_ajuste_funcional1 = this.formEdit.ajuste_funcional1 || 0
            this.v_ajuste_promedio1 = this.formEdit.ajuste_promedio1 || 0
            this.v_ajuste_interna1 = this.formEdit.ajuste_interna1 || 0
            this.v_ajuste_funcional2 = this.formEdit.ajuste_funcional2 || 0
            this.v_ajuste_promedio2 = this.formEdit.ajuste_promedio2 || 0
            this.v_ajuste_interna2 = this.formEdit.ajuste_interna2 || 0
            this.showDialogEdit = true
        },

        calcularAjustes() {
            const saldo = parseFloat(this.formEdit.saldo_actual) || 0
            const ajuste = parseFloat(this.v_ajuste_actual) || 0
            if (saldo === 0) {
                this.v_ajuste_funcional1 = ajuste
                this.v_ajuste_promedio1 = ajuste
                this.v_ajuste_interna1 = ajuste
                this.v_ajuste_funcional2 = ajuste
                this.v_ajuste_promedio2 = ajuste
                this.v_ajuste_interna2 = ajuste
                return
            }
            const porcentaje = ajuste / saldo
            this.v_ajuste_funcional1 = Math.round((this.formEdit.funcional1 || 0) * porcentaje * 100) / 100
            this.v_ajuste_promedio1 = Math.round((this.formEdit.promedio1 || 0) * porcentaje * 100) / 100
            this.v_ajuste_interna1 = Math.round((this.formEdit.interna1 || 0) * porcentaje * 100) / 100
            this.v_ajuste_funcional2 = Math.round((this.formEdit.funcional2 || 0) * porcentaje * 100) / 100
            this.v_ajuste_promedio2 = Math.round((this.formEdit.promedio2 || 0) * porcentaje * 100) / 100
            this.v_ajuste_interna2 = Math.round((this.formEdit.interna2 || 0) * porcentaje * 100) / 100
        },

        restaurarAjuste() {
            this.v_ajuste_actual = -(this.formEdit.saldo_actual || 0)
            this.calcularAjustes()
        },

        async guardarAjuste() {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para edición.' : 'The validity is disabled for editing.')
                return
            }
            this.showDialogEdit = false
            const data = {
                ...this.formEdit,
                ajuste_actual: this.v_ajuste_actual,
                ajuste_funcional1: this.v_ajuste_funcional1,
                ajuste_promedio1: this.v_ajuste_promedio1,
                ajuste_interna1: this.v_ajuste_interna1,
                ajuste_funcional2: this.v_ajuste_funcional2,
                ajuste_promedio2: this.v_ajuste_promedio2,
                ajuste_interna2: this.v_ajuste_interna2,
            }
            try {
                await Balance.grabeAjuste("O", data)
                this.$toast.success(this.$store.state._language == "es-ES" ? "Información Guardada" : "Information Saved")
                this.$emit('ajusteGuardado')
                this.calcularDiferencia()
            } catch (error) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al guardar" : "Error saving")
            }
        },

        async eliminarAjuste() {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para eliminar.' : 'The validity is disabled for deleting.')
                return
            }
            this.showDialogEdit = false
            try {
                await Balance.elimineAjuste("O", this.formEdit)
                this.$toast.success(this.$store.state._language == "es-ES" ? "Información Eliminada" : "Information Deleted")
                this.$emit('ajusteGuardado')
                this.$emit('cierraDetalle')
                this.calcularDiferencia()
            } catch (error) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al eliminar" : "Error deleting")
            }
        },

        // =============================================
        // ABRIR CONTRA AJUSTE
        // =============================================
        abrirContraAjuste() {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para ajustar.' : 'The validity is disabled for adjusting.')
                return
            }

            let totalOrigen = 0;
            let totalDestino = 0;
            
            // Sumar ajustes de ORIGEN
            if (this.ajustOrigen) {
                this.ajustOrigen.forEach(item => {
                    if (item.origen !== "C") {
                        totalOrigen += parseFloat(item.ajuste_funcional1) || 0;
                    }
                });
            }

            // Sumar ajustes de DESTINO
            if (this.$parent && this.$parent.ajusteDesti) {
                this.$parent.ajusteDesti.forEach(item => {
                    if (item.origen !== "C") {
                        totalDestino += parseFloat(item.ajuste_funcional1) || 0;
                    }
                });
            }

            this.totalOrigen = totalOrigen;
            this.totalDestino = totalDestino;
            // La diferencia es: -(totalOrigen + totalDestino)
            this.diferenciaTotal = -(totalOrigen + totalDestino);

            this.cuentasOrigen = this.ajustOrigen ? this.ajustOrigen.filter(item => item.origen !== "C").length : 0;

            this.formAjuste.origen = true;
            this.formAjuste.destino = false;
            this.showSummary = true;

            if (Math.abs(this.diferenciaTotal) > 0.01) {
                this.showContraAjuste = true;
            } else {
                this.$toast.info(this.$store.state._language == "es-ES" ? "No hay diferencia para ajustar (la suma ya es 0)" : "No difference to adjust (sum is already 0)");
            }
        },

        seleccionarOrigen() {
            this.formAjuste.origen = true
            this.formAjuste.destino = false
        },

        seleccionarDestino() {
            this.formAjuste.origen = false
            this.formAjuste.destino = true
        },

        // =============================================
        // APLICAR AJUSTE AUTOMÁTICO
        // =============================================
        async aplicarAjusteAutomatico() {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para ajustar.' : 'The validity is disabled for adjusting.')
                return
            }

            // Recalcular diferencia antes de aplicar
            this.calcularDiferencia();

            if (Math.abs(this.diferenciaTotal) < 0.01) {
                this.$toast.info(this.$store.state._language == "es-ES" ? "No hay diferencia para ajustar" : "No difference to adjust")
                this.showContraAjuste = false                
                return
            }

            try {
                if (this.formAjuste.origen) {
                    const cuentasAjustables = this.ajustOrigen ? this.ajustOrigen.filter(item => item.origen !== "C") : [];

                    if (cuentasAjustables.length === 0) {
                        this.$toast.error(this.$store.state._language == "es-ES" ? "No hay cuentas de origen para ajustar" : "No origin accounts to adjust")
                        return
                    }

                    const valorPorCuenta = this.diferenciaTotal / cuentasAjustables.length;

                    for (const cuenta of cuentasAjustables) {
                        await Balance.grabeAjuste("O", {
                            ...cuenta,
                            ajuste_actual: valorPorCuenta,
                            ajuste_funcional1: valorPorCuenta,
                            ajuste_promedio1: valorPorCuenta,
                            ajuste_interna1: valorPorCuenta,
                            ajuste_funcional2: valorPorCuenta,
                            ajuste_promedio2: valorPorCuenta,
                            ajuste_interna2: valorPorCuenta
                        });
                    }

                    this.$toast.success(this.$store.state._language == "es-ES"
                        ? `Ajuste distribuido equitativamente en ${cuentasAjustables.length} cuentas del ORIGEN`
                        : `Adjustment distributed equally in ${cuentasAjustables.length} origin accounts`
                    )

                } else if (this.formAjuste.destino) {
                    let cuentasDestino = [];
                    if (this.$parent && this.$parent.ajusteDesti) {
                        cuentasDestino = this.$parent.ajusteDesti.filter(item => item.origen !== "C");
                    }

                    if (cuentasDestino.length === 0) {
                        this.$toast.error(this.$store.state._language == "es-ES" ? "No hay cuentas de destino para ajustar" : "No destination accounts to adjust")
                        return
                    }

                    const valorPorCuenta = this.diferenciaTotal / cuentasDestino.length;

                    for (const cuenta of cuentasDestino) {
                        await Balance.grabeAjuste("D", {
                            ...cuenta,
                            ajuste_actual: valorPorCuenta,
                            ajuste_funcional1: valorPorCuenta,
                            ajuste_promedio1: valorPorCuenta,
                            ajuste_interna1: valorPorCuenta,
                            ajuste_funcional2: valorPorCuenta,
                            ajuste_promedio2: valorPorCuenta,
                            ajuste_interna2: valorPorCuenta
                        });
                    }

                    this.$toast.success(this.$store.state._language == "es-ES"
                        ? `Ajuste distribuido equitativamente en ${cuentasDestino.length} cuentas del DESTINO`
                        : `Adjustment distributed equally in ${cuentasDestino.length} destination accounts`
                    )
                }

                this.showContraAjuste = false
                this.$emit('ajusteGuardado')
                this.calcularDiferencia()

            } catch (error) {
                console.error('Error en aplicarAjusteAutomatico:', error)
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al aplicar el ajuste" : "Error applying adjustment")
            }
        },

        // =============================================
        // AJUSTAR TODO
        // =============================================
        async ajustarTodo() {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para ajustar.' : 'The validity is disabled for adjusting.')
                return
            }

            if (!this.ajustOrigen || this.ajustOrigen.length === 0) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "No hay registros para ajustar" : "No records to adjust")
                return
            }

            const confirmar = confirm(this.$store.state._language == "es-ES"
                ? `¿Ajustar todos los registros? (${this.ajustOrigen.length} registros)`
                : `Adjust all records? (${this.ajustOrigen.length} records)`
            )
            if (!confirmar) return

            await this.ejecutarAjusteTotal()
        },

        // =============================================
        // EJECUTAR AJUSTE TOTAL
        // =============================================
        async ejecutarAjusteTotal() {
            let registrosAjustados = 0
            let errores = 0
            let registrosOmitidos = 0

            for (const item of this.ajustOrigen) {
                if (item.origen === "C") continue

                const ajusteActualExistente = parseFloat(item.ajuste_actual)
                const tieneAjusteManual = !isNaN(ajusteActualExistente) &&
                    (ajusteActualExistente > 1 || ajusteActualExistente < -1)

                if (tieneAjusteManual) {
                    registrosOmitidos++
                    continue
                }

                try {
                    const ajusteActual = -(item.saldo_actual || 0)
                    await Balance.grabeAjuste("O", {
                        ...item,
                        ajuste_actual: ajusteActual,
                        ajuste_funcional1: -(item.funcional1 || 0),
                        ajuste_promedio1: -(item.promedio1 || 0),
                        ajuste_interna1: -(item.interna1 || 0),
                        ajuste_funcional2: -(item.funcional2 || 0),
                        ajuste_promedio2: -(item.promedio2 || 0),
                        ajuste_interna2: -(item.interna2 || 0)
                    })
                    registrosAjustados++
                } catch (error) {
                    errores++
                    console.error("Error ajustando registro:", error)
                }
            }

            this.calcularDiferencia()

            if (errores === 0) {
                if (registrosOmitidos > 0) {
                    this.$toast.success(this.$store.state._language == "es-ES"
                        ? `Se ajustaron ${registrosAjustados} registros y se omitieron ${registrosOmitidos} con ajuste existente`
                        : `Adjusted ${registrosAjustados} records and skipped ${registrosOmitidos} with existing adjustment`
                    )
                } else {
                    this.$toast.success(this.$store.state._language == "es-ES"
                        ? `Se ajustaron ${registrosAjustados} registros correctamente`
                        : `Adjusted ${registrosAjustados} records successfully`
                    )
                }
            } else {
                this.$toast.warning(this.$store.state._language == "es-ES"
                    ? `Se ajustaron ${registrosAjustados} registros, se omitieron ${registrosOmitidos} y hubo ${errores} errores`
                    : `Adjusted ${registrosAjustados} records, skipped ${registrosOmitidos} and ${errores} errors`
                )
            }

            this.$emit('ajusteGuardado')
        }
    },
    watch: {
        ajustOrigen: {
            handler() {
                this.calcularDiferencia()
            },
            deep: true
        }
    },
    mounted() {
        this.calcularDiferencia()
    }
}
</script>

<style src="../main.css"></style>
<style scoped>
.ajustes-detail-container { padding: 0; width: 100%; }
.ajustes-detail-table-container { width: 100%; }
.ajustes-detail-table { width: 100%; overflow-x: auto; }
.detail-toolbar-left { flex: 1; min-width: 0; }
.detail-toolbar-right { flex: 0 0 auto; display: flex; align-items: center; gap: 4px; }

.table-action-btn { border: none; border-radius: 4px; transition: all 0.2s; }
.table-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
}

.table-toolbar-add-btn-origin-ajuste {
    background-color: #28a745;
    color: white;
    border: 1px solid #28a745;
}
.table-toolbar-add-btn-origin-ajuste:hover:not(:disabled) { background-color: #218838; border-color: #1e7e34; }

.table-toolbar-add-btn-origin-all {
    background-color: #ffc107;
    color: #212529;
    border: 1px solid #ffc107;
}
.table-toolbar-add-btn-origin-all:hover:not(:disabled) { background-color: #e0a800; border-color: #d39e00; }

.admin-table-block { background: white; border-radius: 8px; padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
.admin-table-block h5 { color: #6c757d; text-align: center; margin: 0; }

@media (max-width: 768px) {
    .ajustes-detail-container { min-height: 450px; }
    .detail-toolbar-left h5 { font-size: 0.8rem; }
    .table-toolbar-add-btn { font-size: 11px; padding: 0 10px; height: 28px; min-height: 28px; }
}

@media (max-width: 576px) {
    .ajustes-detail-container { min-height: 400px; }
    .detail-toolbar-left { flex-wrap: wrap; gap: 4px; }
    .detail-toolbar-right { flex-wrap: wrap; gap: 4px; }
    .table-toolbar-add-btn { font-size: 10px; padding: 0 8px; height: 26px; min-height: 26px; }
}
</style>